// @flow
import React, { PureComponent, type Node } from "react";
import { compose } from "redux";
import { connect, Dispatch } from "react-redux";
import noop from "lodash/noop";
import isEqual from "lodash/isEqual";
import LayoutLoader from "containers/LayoutLoader";
import {
    RefreshMode,
    type DocListFilterRequestDTO,
    type DocListSortRequestDTO,
    SortDirectionOptions,
    type DocListLayout,
    type ListViewModeEnum,
    ListViewMode,
    ServerEventItemType,
    type TrackCurrentRowProps,
} from "data/types";
import { type docsResultState } from "data/reducers/types";
import {
    getDocsAction,
    togglesUpdateCounterAction,
    setDocsLayoutAction,
} from "data/actions";
import DocListFilterBar from "components/DocListFilterBar";
import DocList from "components/DocList";
import DocListSimple from "components/DocListSimple";
import DocListFilterModal from "components/DocListFilterModal";
import DocClickHandler from "containers/DocClickHandler";
import {
    IsFiltered,
    GetDefaultFilters,
} from "components/DocListFilterModal/Utils";
import Constants, { type ConstantsEnum } from "data/constants";
import * as s from "data/reducers/selectors";
import GlobalEventsHandler from "containers/GlobalEventsHandler";
import UploadHandler from "containers/UploadHandler";
import Status from "components/Status";
import NoDocs from "components/NoDocs";
import withOfflineHandler from "containers/OfflineHandler";
import withDefaultFiltersHandler from "containers/DefaultFiltersHandler";
import { type MultiSelectionProps } from "containers/MultiSelection";
import localForage from "localforage";

type Props = {
    /** folder itemUri to show */
    itemUri: string,
    /** whether to popup filter modal */
    showFilter: boolean,
    /** whether to automatically load and show documents */
    showDocs: boolean,
    /** whether to show the AddDocument action when NoDocs found
     * @default {boolean} true
     */
    showAddDocument?: boolean,
    /** callback when user closes filter modal */
    onFilterClose: () => void,
    /** callback when user clicks document icon
     * @param {SyntheticMouseEvent} e HTML click event
     */
    onIconClick?: (e: SyntheticMouseEvent) => void,
    /** callback when user single clicks the whole row
     * @param {SyntheticMouseEvent} e HTML click event
     * @param {string} itemUri the row's itemUri
     * @param {number} index the row's index
     */
    onSingleClick?: (
        e: SyntheticMouseEvent,
        itemUri: string,
        index: number
    ) => void,
    /** callback when user double clicks the whole row
     * @param {SyntheticMouseEvent} e HTML click event
     * @param {string} itemUri the row's itemUri
     * @param {number} index the row's index
     */
    onDoubleClick?: (
        e: SyntheticMouseEvent,
        itemUri: string,
        index: number
    ) => void,
    /** callback when user clicks on More... button
     * @param {SyntheticMouseEvent} e HTML click event
     * @param {string} itemUri the row's itemUri
     * @param {number} index the row's index
     */
    onMoreClick?: (
        e: SyntheticMouseEvent,
        itemUri: string,
        index: number
    ) => void,
    /** whether doclist filters should be rendered */
    renderFilter: ?boolean,
    /** whether to disable the More... button */
    actionsDisabled?: boolean,
    /** callback when user right clicks the whole row
     * @param {SyntheticMouseEvent} e HTML click event
     * @param {number} rowIndex the row's index
     * @param {number} columnsIndex the column's index
     * @param {string} itemUri the row's itemUri
     */
    onContextClick?: (
        e: SyntheticMouseEvent,
        rowIndex: number,
        columnIndex: number,
        itemUri: string
    ) => void,
    activeRowIndex?: number,
    scrollToRow?: number,
    /** React.Node children */
    children?: Node,

    // from redux connect()
    layout?: DocListLayout,
    /** current viewName */
    viewName: ?string,
    breadcrumbName: string,
    result?: docsResultState,
    status: ConstantsEnum,
    /** GlobalEventsHandler.refreshId */
    refreshId: number,
    /** Redux dispatch */
    dispatch: Dispatch,
    /** current docListViewMode */
    docListViewMode: ListViewModeEnum,
    /** redux: current formatId */
    formatId: number,
    /** TrackCurrentRow: tracks current selected document row */
    docTracker: ?TrackCurrentRowProps,
    /** withMultipleSelection additional props */
    multiSelection: MultiSelectionProps,
    /** is the doclist used for flatAU */
    forFlatAU: boolean,
};

type State = {
    rowStartIndex: number, //current top row rendered
    scrollToRow: number,
    focusOnFilterName: ?string,
};

export class DocListContainer extends PureComponent<Props, State> {
    static defaultProps = {
        showFilter: false,
        showDocs: true,
        onFilterClose: undefined,
        status: Constants.LOADING,
        dispatch: noop,
    };

    constructor(props: Props) {
        super(props);

        this.state = {
            focusOnFilterName: null,
            rowStartIndex: 0,
            scrollToRow: props.scrollToRow || props.activeRowIndex || -1,
            columnMapper: [...(props.result?.cols?.keys() || [])],
        };
        this.setupColumns(props.result);
    }

    async setupColumns(result) {
        if (!result || !result.cols) return;

        const formatId = this.props.formatId;

        const columnMapper = await this.getColOrder();

        /* istanbul ignore next */
        const storedColWidths =
            (await localForage.getItem(
                `docListLayout_${btoa(this.props.formatId)}`
            )) || {};

        // there's a possibility that by the time we get the storedColWidths, the formatId has changed
        // in that case, we don't want to apply the storedColWidths
        /* istanbul ignore else */
        if (formatId === this.props.formatId) {
            // store the formatId so we can check it later
            storedColWidths.__folderFormatId = formatId;
            this.setState({ columnMapper, storedColWidths });
        }
    }

    getColOrder = async () => {
        const storedOrder = await localForage.getItem(
            `docListLayout_${btoa(this.props.formatId)}_order`
        );

        if (storedOrder?.length === this.props.result?.cols.length) {
            return storedOrder;
        }

        // clear stored order if the number of columns has changed
        /* istanbul ignore else */
        if (
            storedOrder &&
            // cols.length can be 0 during initial load, in that case we don't want to clear the order
            this.props.result?.cols?.length &&
            storedOrder.length !== this.props.result.cols?.length
        ) {
            console.warn(
                "Column order cannot be applied, because the number of columns has changed. Resetting column order."
            );
            localForage.removeItem(
                `docListLayout_${btoa(this.props.formatId)}_order`
            );
        }

        return [...(this.props.result?.cols?.keys() ?? [])];
    };

    onReorder = (oldIndex, newIndex) => {
        const newColumnMapper = [...this.state.columnMapper];
        const [removed] = newColumnMapper.splice(oldIndex, 1);
        newColumnMapper.splice(newIndex, 0, removed);

        localForage.setItem(
            `docListLayout_${btoa(this.props.formatId)}_order`,
            newColumnMapper
        );

        this.setState({ columnMapper: newColumnMapper });
    };

    componentDidMount() {
        /* istanbul ignore else */
        if (
            this.props.showDocs === true ||
            this.props.status === Constants.NONE
        ) {
            this._fetch(this.props.itemUri);
        }
    }

    componentDidUpdate(prevProps: Props) {
        // reload data?
        /* istanbul ignore else */
        if (this.props.showDocs === true && this.props.itemUri !== undefined) {
            const prevNotInitialFilters = prevProps.layout?.filters?.length;

            const isLayoutChanged =
                prevProps.layout?.filters &&
                this.props.layout?.filters &&
                !isEqual(prevProps.layout, this.props.layout);

            const notDefaultFilters =
                this.props.layout?.filters &&
                this.props.result?.cols &&
                !isEqual(
                    this.props.layout.filters,
                    GetDefaultFilters(this.props.result.cols)
                );

            /* istanbul ignore else */
            if (
                prevProps.showDocs !== this.props.showDocs ||
                prevProps.itemUri !== this.props.itemUri ||
                (isLayoutChanged &&
                    (prevNotInitialFilters || notDefaultFilters))
            ) {
                this.setState(
                    {
                        scrollToRow: this.props.activeRowIndex,
                        rowStartIndex: 0,
                    },
                    () => this._fetch(this.props.itemUri)
                );
            }

            /* istanbul ignore else */
            if (
                this.props.docTracker &&
                isLayoutChanged &&
                (prevNotInitialFilters || notDefaultFilters)
            ) {
                this.props.docTracker.setCurrentRow(-1);
                this.setState({ scrollToRow: -1 });
            }
        }

        if (prevProps.itemUri !== this.props.itemUri) {
            this.props.multiSelection?.disable();
        }

        // update totalCount
        if (
            prevProps.result !== this.props.result &&
            this.props.result != null &&
            this.props.viewName != null
        ) {
            /* istanbul ignore else */
            if (prevProps.result?.rows !== this.props.result.rows)
                this._updateToggleCounter(
                    this.props.result.totalCount,
                    Constants.OK
                );

            // Update multiSelection available Rows
            /* istanbul ignore next */
            this.props.multiSelection?.setAvailableRows(this.props.result.rows);
            this.props.pagination?.onRowsUpdate(this.props.result);

            this.setupColumns(this.props.result);
        }

        if (
            this.props.status === Constants.ERROR &&
            prevProps.status !== this.props.status &&
            this.props.viewName != null
        ) {
            this.props.dispatch(
                togglesUpdateCounterAction({
                    name: this.props.viewName,
                    counts: { doclist: 0 },
                    status: { doclist: Constants.ERROR },
                })
            );
        }

        // finished uploading? refresh
        /* istanbul ignore else */
        if (prevProps.refreshId !== this.props.refreshId) {
            this.props.docTracker?.setCurrentRow(-1);
            this._fetch(this.props.itemUri);
            this.props.multiSelection?.disable();
            this.props.pagination?.onRowsUpdate(this.props.result);
        }
    }

    _updateToggleCounter(count: number, doclist?: ConstantsEnum): void {
        const { dispatch, viewName, layout } = this.props;
        let status = undefined;
        /* istanbul ignore else */
        if (layout?.filters && IsFiltered(layout.filters)) {
            status = {
                doclist: doclist !== Constants.LOADING ? "filter" : doclist,
            };
        } else if (doclist != null) {
            status = { doclist };
        }
        /* istanbul ignore else */
        if (viewName != null)
            dispatch(
                togglesUpdateCounterAction({
                    name: viewName,
                    counts: { doclist: count },
                    status,
                })
            );
    }

    // load next page of results
    _loadMoreRows = (pageNum: number) => {
        /* istanbul ignore else */
        if (
            this.props.itemUri !== undefined &&
            pageNum > 0 &&
            pageNum < Infinity // HACK: do not fetch Cloud requests with Infinity pageNum
        )
            this._fetch(this.props.itemUri, pageNum);
    };

    // load first page of documents
    _fetch(itemUri: string, pageNum: number = null) {
        const { dispatch, showDocs, layout } = this.props;
        if ((pageNum ?? 0) === 0 && showDocs)
            this._updateToggleCounter(0, Constants.LOADING);
        dispatch(
            getDocsAction({
                pageNum: showDocs ? pageNum ?? 0 : -1,
                itemUri,
                sorts: layout?.sorts,
                filters: layout?.filters,
            })
        );
    }

    _setSorting = (name: string) => {
        const { layout } = this.props;
        let direction = SortDirectionOptions.Ascending;
        if (layout?.sorts?.[0]?.name === name) {
            direction =
                layout.sorts[0].direction === SortDirectionOptions.Ascending
                    ? SortDirectionOptions.Descending
                    : SortDirectionOptions.Ascending;
        }

        const sorts: Array<DocListSortRequestDTO> = [
            {
                name,
                direction,
            },
        ];
        this.props.dispatch(setDocsLayoutAction({ sorts }));
    };

    _onFilter = (filters: Array<DocListFilterRequestDTO>) => {
        this._onFilterClose();
        this.props.dispatch(setDocsLayoutAction({ filters }));
    };

    _onFilterClose = () => {
        this.setState({ focusOnFilterName: null });
        /* istanbul ignore else */
        if (typeof this.props.onFilterClose === "function")
            this.props.onFilterClose();
    };

    /* istanbul ignore next */ _requestShowFilter = (name) =>
        this.setState({
            focusOnFilterName: name,
        });

    /* istanbul ignore next */ _rowStartIndexUpdated = (rowStartIndex) =>
        this.setState({ rowStartIndex });

    _onContextClick = (
        e,
        rowIndex: number,
        columnIndex: number,
        itemUri: string
    ) => this.props.onMoreClick(e, itemUri, rowIndex, columnIndex);

    // temporary fix for multiSelection (to get latest values), when upgrade component to functional => check if it's still necessary
    _isRowActive = (multiple: boolean) => (rowIndex: number, activeState: boolean): boolean => {
        return multiple
            ? this.props.multiSelection?.isSelected(rowIndex)
            : activeState;
    };

    render() {
        const {
            status,
            showDocs,
            showFilter,
            renderFilter,
            result,
            hasFilter,
            layout,
            breadcrumbName,
            docListViewMode,
        } = this.props;

        if (showDocs) {
            return (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        width: "100%",
                    }}
                >
                    <DocListFilterBar
                        renderFilter={renderFilter}
                        disableFilter={
                            (!result || result.totalCount === 0) && !hasFilter
                        }
                        layout={layout}
                        setSorting={this._setSorting}
                        requestShowFilter={this._requestShowFilter}
                    />

                    <DocListFilterModal
                        show={
                            (showFilter ||
                                this.state.focusOnFilterName !== null) &&
                            /* istanbul ignore next */
                            (status === Constants.OK || result != null)
                        }
                        focusOnFilterName={this.state.focusOnFilterName}
                        onClose={this._onFilterClose}
                        onFilter={this._onFilter}
                        result={result}
                        title={breadcrumbName}
                        filters={layout?.filters}
                        formatId={this.props.formatId}
                        forFlatAU={this.props.forFlatAU}
                    />
                    {status !== Constants.OK &&
                        (result == null || result.totalCount === 0) && (
                            <Status
                                status={status}
                                error={
                                    this.props.error ||
                                    this.props.offlineHandler.error
                                }
                            />
                        )}
                    {status === Constants.OK &&
                        result != null &&
                        result.totalCount === 0 && (
                            <NoDocs
                                layout={layout}
                                showAddDocument={this.props.showAddDocument}
                                requestShowFilter={this._requestShowFilter}
                            />
                        )}
                    {result != null && result.totalCount > 0 && (
                        <>
                            {(docListViewMode === ListViewMode.List ||
                                docListViewMode ===
                                    ListViewMode.Thumbnails) && (
                                <DocListSimple
                                    result={result}
                                    pageNum={this.props.pageNum}
                                    totalCount={result.totalCount}
                                    loadMoreRows={this._loadMoreRows}
                                    layout={layout}
                                    showThumbnails={
                                        docListViewMode ===
                                        ListViewMode.Thumbnails
                                    }
                                    scrollToRow={this.state.scrollToRow}
                                    activeRowIndex={this.props.activeRowIndex}
                                    onIconClick={this.props.onIconClick}
                                    onSingleClick={this.props.onSingleClick}
                                    onDoubleClick={this.props.onDoubleClick}
                                    onContextClick={this._onContextClick}
                                    onMoreClick={this.props.onMoreClick}
                                    requestShowFilter={this._requestShowFilter}
                                    rowStartIndexUpdated={
                                        this._rowStartIndexUpdated
                                    }
                                >
                                    {this.props.children}
                                </DocListSimple>
                            )}
                            {docListViewMode === ListViewMode.Grid &&
                                result.cols.length && (
                                    <DocList
                                        result={result}
                                        cols={result.cols}
                                        totalCount={result.totalCount}
                                        loadMoreRows={this._loadMoreRows}
                                        setSorting={this._setSorting}
                                        layout={layout}
                                        onIconClick={this.props.onIconClick}
                                        onSingleClick={this.props.onSingleClick}
                                        onDoubleClick={this.props.onDoubleClick}
                                        onContextClick={this._onContextClick}
                                        onMoreClick={this.props.onMoreClick}
                                        requestShowFilter={
                                            this._requestShowFilter
                                        }
                                        rowStartIndexUpdated={
                                            this._rowStartIndexUpdated
                                        }
                                        scrollToRow={this.state.scrollToRow}
                                        activeRowIndex={
                                            this.props.activeRowIndex
                                        }
                                        isRowActive={this._isRowActive(this.props.multiSelection?.multiple)}
                                        itemUri={this.props.itemUri}
                                        onReorder={this.onReorder}
                                        columnMapper={this.state.columnMapper}
                                        colWidths={this.state.storedColWidths}
                                        formatId={this.props.formatId}
                                        actionsDisabled={
                                            this.props.actionsDisabled
                                        }
                                        viewName={this.props.viewName}
                                    >
                                        {this.props.children}
                                    </DocList>
                                )}
                        </>
                    )}
                </div>
            );
        } else if (showFilter || this.state.focusOnFilterName != null) {
            if (
                status === Constants.OK &&
                result != null &&
                result.filters != null
            ) {
                return (
                    <DocListFilterModal
                        show={
                            showFilter || this.state.focusOnFilterName != null
                        }
                        focusOnFilterName={this.state.focusOnFilterName}
                        onClose={this._onFilterClose}
                        onFilter={this._onFilter}
                        result={result}
                        title={breadcrumbName}
                        filters={layout?.filters}
                        formatId={this.props.formatId}
                        forFlatAU={this.props.forFlatAU}
                    />
                );
            } else return <Status status={Constants.LOADING} />;
        } else
            return (
                this.props.children || (
                    <Status
                        status={Constants.ERROR}
                        error={
                            this.props.error || this.props.offlineHandler.error
                        }
                    />
                )
            );
    }
}

const mapStateToProps = /* istanbul ignore next */ (state, ownProps) => ({
    breadcrumbName: s.breadcrumbNameSelector(
        state,
        s.currentNameSelector(state)
    ),
    status: s.docsStatusSelector(state),
    error: s.docsErrorSelector(state),
    docListViewMode: s.docListViewModeSelector(state),
    formatId: s.breadcrumbFormatIdSelector(state, s.currentNameSelector(state)),
    result: state.docs,
    pageNum: s.docsPageNumSelector(state),
    hasFilter: (s.docsLayoutFiltersSelector(state) || []).filter(
        (filter) => filter.values.filter((v) => v).length
    ).length,
    ...ownProps,
});

export const DocListWithClickHandlerFromProps = compose(
    LayoutLoader,
    connect(mapStateToProps),
    GlobalEventsHandler({
        items: [ServerEventItemType.document, ServerEventItemType.activity],
        refreshMode: RefreshMode.Originator,
        refreshMessage: "$globalEvents:refresh.doclist",
    }),
    UploadHandler(),
    withOfflineHandler,
    withDefaultFiltersHandler
)(DocListContainer);

const DocListWithClickHandler = compose(
    LayoutLoader,
    connect(mapStateToProps),
    GlobalEventsHandler({
        items: [ServerEventItemType.document, ServerEventItemType.activity],
        refreshMode: RefreshMode.Originator,
        refreshMessage: "$globalEvents:refresh.doclist",
    }),
    UploadHandler(),
    withOfflineHandler,
    withDefaultFiltersHandler,
    DocClickHandler
)(DocListContainer);
DocListWithClickHandler.displayName = "DocListContainer";

export default DocListWithClickHandler;
